import { XIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { useCreateDonationsOfflineDonationMutation } from "../../../../state/domainRouterApi";
import { TailwindDateField } from "../../../../components/forms/TailwindDateField";

const PRIMARY = "#2697a2";
const TEXT = "#404f4d";
const BORDER = "#e5e7eb";

type OfflineDonationFormState = {
  targetDate: string;
  donorName: string;
  email: string;
  checkNumber: string;
  amount: string;
  dedicationType: string;
  honoreeName: string;
};

const EMPTY_FORM: OfflineDonationFormState = {
  targetDate: new Date().toISOString().slice(0, 10),
  donorName: "",
  email: "",
  checkNumber: "",
  amount: "",
  dedicationType: "",
  honoreeName: "",
};

const DEDICATION_TYPES = [
  { value: "", label: "None" },
  { value: "HONOR", label: "In Honor Of" },
  { value: "MEMORY", label: "In Memory Of" },
];

const inputClasses =
  "block w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2";

function FormField({
  label,
  htmlFor,
  required,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="mb-1.5 block text-sm font-medium"
        style={{ color: TEXT }}
      >
        {label}
        {required ? <span className="ml-1 text-red-500">*</span> : null}
      </label>
      {children}
    </div>
  );
}

export function OfflineDonationModal({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [createOffline, { isLoading: submitting }] =
    useCreateDonationsOfflineDonationMutation();
  const [form, setForm] = useState<OfflineDonationFormState>(EMPTY_FORM);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = event.target;
      setForm((current) => ({ ...current, [name]: value }));
      setSubmitError(null);
    },
    [],
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    const amountCents = Math.round(Number.parseFloat(form.amount) * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      setSubmitError("Please enter a valid donation amount.");
      return;
    }

    const [donorFirstName, ...donorLastParts] = form.donorName
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    const donorLastName = donorLastParts.join(" ").trim() || "Donor";

    const input: Record<string, unknown> = {
      targetGregorianDate: form.targetDate,
      donorFirstName: donorFirstName || "Anonymous",
      donorLastName,
      donorEmail: form.email.trim() || undefined,
      checkNumber: form.checkNumber.trim() || undefined,
      grossCents: amountCents,
      dedicationType: form.dedicationType || undefined,
      honoreeName: form.honoreeName.trim() || undefined,
    };

    try {
      await createOffline(input).unwrap();
      setForm(EMPTY_FORM);
      onSuccess();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : err && typeof err === "object" && "message" in err
            ? String((err as { message?: unknown }).message)
            : "Failed to record offline donation. The donations backend may not be deployed.";
      setSubmitError(message);
    }
  };

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setSubmitError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Add offline donation"
    >
      <div
        data-passive-click-surface="true"
        className="fixed inset-0 bg-black/40"
        onClick={handleClose}
        onKeyDown={(event) => {
          if (event.key === "Escape") handleClose();
        }}
        role="button"
        tabIndex={-1}
        aria-label="Close modal"
      />
      <div
        className="relative mx-4 w-full max-w-[520px] overflow-y-auto rounded-2xl bg-white shadow-xl"
        style={{ maxHeight: "90vh" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: BORDER }}
        >
          <h2 className="text-lg font-semibold" style={{ color: TEXT }}>
            Add Offline Donation
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-full p-1 transition-colors hover:bg-gray-100"
            aria-label="Close"
          >
            <XIcon className="h-5 w-5" style={{ color: TEXT }} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <FormField label="Date" htmlFor="offline-targetDate" required>
            <TailwindDateField
              id="offline-targetDate"
              name="targetDate"
              required
              value={form.targetDate}
              onChange={handleChange}
              className={inputClasses}
              style={{ borderColor: BORDER, color: TEXT }}
            />
          </FormField>

          <FormField label="Donor Name" htmlFor="offline-donorName" required>
            <input
              id="offline-donorName"
              name="donorName"
              type="text"
              required
              value={form.donorName}
              onChange={handleChange}
              className={inputClasses}
              style={{ borderColor: BORDER, color: TEXT }}
              placeholder="Cohen Family"
            />
          </FormField>

          <FormField label="Email" htmlFor="offline-email">
            <input
              id="offline-email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              className={inputClasses}
              style={{ borderColor: BORDER, color: TEXT }}
              placeholder="donor@example.com"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Check Number" htmlFor="offline-checkNumber">
              <input
                id="offline-checkNumber"
                name="checkNumber"
                type="text"
                value={form.checkNumber}
                onChange={handleChange}
                className={inputClasses}
                style={{ borderColor: BORDER, color: TEXT }}
                placeholder="1234"
              />
            </FormField>

            <FormField label="Amount ($)" htmlFor="offline-amount" required>
              <input
                id="offline-amount"
                name="amount"
                type="number"
                min="0.01"
                step="0.01"
                required
                value={form.amount}
                onChange={handleChange}
                className={inputClasses}
                style={{ borderColor: BORDER, color: TEXT }}
                placeholder="180.00"
              />
            </FormField>
          </div>

          <FormField label="Dedication Type" htmlFor="offline-dedicationType">
            <select
              id="offline-dedicationType"
              name="dedicationType"
              value={form.dedicationType}
              onChange={handleChange}
              className={inputClasses}
              style={{ borderColor: BORDER, color: TEXT }}
            >
              {DEDICATION_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </FormField>

          {form.dedicationType ? (
            <FormField label="Honoree Name" htmlFor="offline-honoreeName">
              <input
                id="offline-honoreeName"
                name="honoreeName"
                type="text"
                value={form.honoreeName}
                onChange={handleChange}
                className={inputClasses}
                style={{ borderColor: BORDER, color: TEXT }}
                placeholder="Rabbi David Cohen"
              />
            </FormField>
          ) : null}

          {submitError ? (
            <div
              className="rounded-xl border px-4 py-3 text-sm"
              style={{
                borderColor: "#fbbf24",
                backgroundColor: "#fffbeb",
                color: "#92400e",
              }}
            >
              {submitError}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors hover:bg-gray-50"
              style={{ borderColor: BORDER, color: TEXT }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              style={{ backgroundColor: PRIMARY }}
            >
              {submitting ? "Saving..." : "Record Donation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
